/**
 * useStageMovePrompt — the promise bridge between the presentation-free card
 * move service and the sensitive-stage reason modal (SE-5495).
 *
 * `useKanbanCardOperations` must not know that a modal exists; it only awaits
 * a resolver. This hook owns that resolver: calling it opens the prompt and
 * returns a promise that stays pending until the user submits (resolves with
 * the decision) or cancels (resolves `null`, which the move service reads as
 * "abandon the move").
 *
 * The prompt is board-local rather than the app-global `CustomPrompt` singleton
 * (mounted once in `src/app/_layout.tsx` and threaded as `promptRef`), which is
 * global and text-only. SE-5496 extends this same prompt with a terminal-outcome
 * selector, so driving the modal off the `{ requiresRationale, requiresOutcome }`
 * gate from day one means that ticket adds a field here rather than swapping the
 * component out.
 *
 * The hook returns its own rendered JSX (`stageMovePromptComponent`) rather than
 * a prop bag — the established kanban convention, matching
 * `useBoardManagement`'s `deleteDialogComponent` and `useTagManagement`. The
 * screen only has to drop it into the tree.
 * @module features/player-kanban/hooks/useStageMovePrompt
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import StageMovePrompt from "../components/molecules/StageMovePrompt";
import type {
  StageMoveDecision,
  StageMoveDecisionResolver,
  StageMoveRequest,
} from "./useKanbanCardOperations";

import type { KanbanCardOutcome } from "@/generated/graphql";

/**
 * Fully-resolved props for the stage-move prompt view.
 */
export interface StageMovePromptProps {
  /** Whether the prompt is visible. */
  readonly isOpen: boolean;
  /** Player being moved, for the prompt copy. */
  readonly playerName?: string;
  /** Destination stage name, for the prompt copy. */
  readonly toPhaseName?: string;
  /** Origin stage name, for the prompt copy. */
  readonly fromPhaseName?: string;
  /** Whether a free-text reason is required. */
  readonly requiresRationale: boolean;
  /**
   * Whether a terminal outcome is required (SE-5496). When true, the prompt
   * renders the outcome selector and a reason is required as well.
   */
  readonly requiresOutcome: boolean;
  /**
   * SE-5496: the board template's ordered valid outcomes to render as the
   * selector's options. Empty for a non-terminal move.
   */
  readonly validOutcomes: readonly KanbanCardOutcome[];
  /** SE-5496: the currently-selected terminal outcome, if any. */
  readonly selectedOutcome?: KanbanCardOutcome;
  /** SE-5496: outcome selection handler. */
  readonly onSelectOutcome: (outcome: KanbanCardOutcome) => void;
  /** Current reason text. */
  readonly rationale: string;
  /** Reason text change handler. */
  readonly onChangeRationale: (value: string) => void;
  /** Whether the confirm action is blocked by an unmet gate. */
  readonly isSubmitDisabled: boolean;
  /** Confirm handler — settles the pending move with the collected answer. */
  readonly onSubmit: () => void;
  /** Cancel handler — settles the pending move as abandoned. */
  readonly onCancel: () => void;
}

/**
 * Return shape of {@link useStageMovePrompt}.
 */
export interface UseStageMovePromptResult {
  /** Inject this into `useKanbanCardOperations`. */
  readonly requestStageMoveDecision: StageMoveDecisionResolver;
  /**
   * Rendered prompt — drop it into the screen's tree. The move path awaits this
   * prompt, so a screen that never mounts it would leave a gated move hanging
   * unanswered.
   */
  readonly stageMovePromptComponent: React.ReactElement;
  /**
   * The same state as fully-resolved view props. Exposed for tests and for any
   * surface that needs to render the prompt inside its own chrome (SE-5494's
   * drawer).
   */
  readonly promptProps: StageMovePromptProps;
}

/** Open-prompt state; `null` while the prompt is closed. */
type PendingPrompt = {
  readonly request: StageMoveRequest;
  readonly rationale: string;
  /** SE-5496: the selected terminal outcome, or undefined until chosen. */
  readonly outcome?: KanbanCardOutcome;
};

/**
 * Owns the sensitive-stage prompt's state and its promise handshake.
 * @returns The injectable resolver plus fully-resolved view props.
 */
const useStageMovePrompt = (): UseStageMovePromptResult => {
  const [pending, setPending] = useState<PendingPrompt | null>(null);

  // The in-flight promise's resolve function. Held in a ref (not state) so the
  // resolver identity below never changes — `movePlayerToPhase` lists the
  // resolver in its dependency array, and a new identity every render would
  // re-memoize the whole move path on every keystroke in the modal.
  const resolveRef = useRef<((value: StageMoveDecision | null) => void) | null>(
    null
  );

  /** Settle whatever prompt is currently open, then close it. */
  const settle = useCallback((value: StageMoveDecision | null) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setPending(null);
    resolve?.(value);
  }, []);

  const requestStageMoveDecision = useCallback<StageMoveDecisionResolver>(
    request =>
      new Promise<StageMoveDecision | null>(resolve => {
        // A second drag while a prompt is open supersedes the first. Settle
        // the older promise as cancelled rather than dropping its resolver:
        // an unsettled promise would leave `movePlayerToPhase` awaiting
        // forever, wedging that move with no override to clean up.
        const previousResolve = resolveRef.current;
        previousResolve?.(null);

        resolveRef.current = resolve;
        // Always reopen with an empty field and no outcome — a reason or
        // outcome from a previous, abandoned move must never carry into the
        // next one.
        setPending({ request, rationale: "", outcome: undefined });
      }),
    []
  );

  const onChangeRationale = useCallback((value: string) => {
    setPending(current =>
      current ? { ...current, rationale: value } : current
    );
  }, []);

  const onSelectOutcome = useCallback((outcome: KanbanCardOutcome) => {
    setPending(current => (current ? { ...current, outcome } : current));
  }, []);

  const onCancel = useCallback(() => {
    settle(null);
  }, [settle]);

  // Reads `pending` from state rather than from inside a `setPending` updater:
  // settling a promise is a side effect, and React may invoke an updater more
  // than once (StrictMode double-render), so the resolve must not live there.
  const onSubmit = useCallback(() => {
    if (!pending) return;

    const gate = pending.request.gate;
    // SE-5496: a terminal move requires a reason too, so the reason is
    // mandatory whenever EITHER a rationale or an outcome is required.
    const reasonRequired = gate.requiresRationale || gate.requiresOutcome;
    const trimmed = pending.rationale.trim();

    // Guard rather than rely solely on the disabled button: a keyboard submit
    // path could otherwise bypass the disabled state.
    if (reasonRequired && !trimmed) return;
    if (gate.requiresOutcome && !pending.outcome) return;

    settle({
      rationale: trimmed || undefined,
      outcome: gate.requiresOutcome ? pending.outcome : undefined,
    });
  }, [pending, settle]);

  const promptProps = useMemo<StageMovePromptProps>(() => {
    const gate = pending?.request.gate;
    const requiresRationale = gate?.requiresRationale ?? false;
    const requiresOutcome = gate?.requiresOutcome ?? false;
    const reasonRequired = requiresRationale || requiresOutcome;
    const rationale = pending?.rationale ?? "";
    const selectedOutcome = pending?.outcome;

    const reasonUnmet = reasonRequired && !rationale.trim();
    const outcomeUnmet = requiresOutcome && !selectedOutcome;

    return {
      isOpen: pending !== null,
      playerName: pending?.request.playerName,
      toPhaseName: pending?.request.toPhaseName,
      fromPhaseName: pending?.request.fromPhaseName,
      requiresRationale,
      requiresOutcome,
      validOutcomes: pending?.request.validOutcomes ?? [],
      selectedOutcome,
      onSelectOutcome,
      rationale,
      onChangeRationale,
      isSubmitDisabled: reasonUnmet || outcomeUnmet,
      onSubmit,
      onCancel,
    };
  }, [pending, onChangeRationale, onSelectOutcome, onSubmit, onCancel]);

  const stageMovePromptComponent = useMemo(
    () => <StageMovePrompt {...promptProps} />,
    [promptProps]
  );

  // On unmount, settle any open prompt as cancelled. Without this, a tree that
  // unmounts while a prompt is open (e.g. navigating away mid-drag into a
  // protected stage) would leave the promise `movePlayerToPhase` awaits pending
  // forever, wedging that move with a `pendingMoves` override that never clears.
  // Cleanup only resolves the promise — no setState on unmount — so it does not
  // trip `react-hooks/set-state-in-effect`.
  useEffect(
    () => () => {
      const resolve = resolveRef.current;
      resolveRef.current = null;
      resolve?.(null);
    },
    []
  );

  return { requestStageMoveDecision, stageMovePromptComponent, promptProps };
};

export default useStageMovePrompt;
